import type { CartesianSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { toDateValue } from '#core/lib/expr/ops/transform/date-utils.ts';
import { type Cell, cellScalar } from '#core/lib/expr/value.ts';

import { compareScalar } from '../cell.ts';
import { bandDataKey } from './band-keys.ts';

/** A recharts-shaped row: the merged x key plus each series/band value. */
export type CartesianRow = Record<string, number | string | null | readonly number[]>;

/**
 * Merge every series + band of a cartesian tile into recharts rows keyed
 * by the shared x value, then order them.
 *
 * `cols` is the flat resolved-column array from `useResolvedColumns`, in
 * series-then-band order: `[s0.x, s0.y, s1.x, s1.y, …, b0.x, b0.lower,
 * b0.upper, …]`. A band contributes a `[lower, upper]` tuple — recharts
 * draws a range Area when a datum's value is a two-element array.
 *
 * Ordering follows the x DOMAIN, not the axis-rendering mode: `time` and
 * `numeric` x are intrinsically ordered (chronological / ascending) and
 * sort even when a bar series makes the axis render as discrete bands —
 * `byX` merges series in arbitrary insertion order, and a bar series can
 * own x-keys the line series never set. Only `category` x keeps caller
 * row order: its labels carry the author's intended sequence (e.g.
 * KPI-ranked bars).
 */
export function cartesianRows(spec: CartesianSpec, cols: readonly (readonly Cell[])[]): CartesianRow[] {
  const seriesRefN = spec.series.length * 2;
  const isTime = spec.xKind === 'time';
  const xKeyOf = (raw: number | string) => (isTime ? (toDateValue(raw)?.getTime() ?? Number.NaN) : raw);

  const byX = new Map<number | string, CartesianRow>();
  spec.series.forEach((s, i) => {
    const xs = cols[2 * i] ?? [];
    const ys = cols[2 * i + 1] ?? [];
    xs.forEach((xv, r) => {
      const rawX = cellScalar(xv);
      if (rawX === null) {
        return;
      }
      const xKey = xKeyOf(rawX);
      const row = byX.get(xKey) ?? { x: xKey };
      row[s.id] = cellScalar(ys[r]);
      byX.set(xKey, row);
    });
  });
  spec.bands.forEach((_b, j) => {
    const off = seriesRefN + 3 * j;
    const xs = cols[off] ?? [];
    const los = cols[off + 1] ?? [];
    const his = cols[off + 2] ?? [];
    xs.forEach((xv, r) => {
      const rawX = cellScalar(xv);
      if (rawX === null) {
        return;
      }
      const lo = cellScalar(los[r]);
      const hi = cellScalar(his[r]);
      const xKey = xKeyOf(rawX);
      const row = byX.get(xKey) ?? { x: xKey };
      // SSOT: the synthetic key here must match the `<Area dataKey>` —
      // `bandDataKey` owns the template for both sides.
      row[bandDataKey(j)] = typeof lo === 'number' && typeof hi === 'number' ? [lo, hi] : null;
      byX.set(xKey, row);
    });
  });
  return spec.xKind === 'category'
    ? [...byX.values()]
    : [...byX.values()].sort((a, b) => compareScalar(a.x as number | string, b.x as number | string));
}
